/**
 * TextInput component module.
 * @module shared/components/TextInput
 *
 */
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components/macro';
import NumberFormat from 'react-number-format';
import {fill, layoutAttribute} from './cssUtils';
import debug from 'debug';
import TitleLabel from './TitleLabel';
import Row from '../../shared/components/Row';
import TaxIDMask from '../../shared/components/TaxIDMask';
import AccountNumberMask from './AccountNumberMask';
import {getBlackWhiteWithOpacity, getHexWithOpacity} from '../utils/colors';

const log = debug('circleblack:components:TextInput');

const baseInputStyles = css`
  text-align: left;
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
  border: 3px solid rgba(255, 65, 65, 0);
  padding: ${props => props.readOnly ? '7px 7px 7px 0px' : '7px'};
  border-radius: 4px;
  background-color: ${props => {
    if (props.theme.neumorphic) {
      return getHexWithOpacity(props.theme.tileBackgroundColor, props.readOnly ? 0 : 1);
    }
    return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  }};
  ${props => props.theme.neumorphic
    ? `background-image: linear-gradient(to bottom right, rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}), rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}))` : ''};
  ${props => props.theme.neumorphic
    ? `box-shadow: -1px -1px 0 0 rgba(0, 0, 0, ${props.readOnly ? 0 : 0.4}),
                   1px 1px 0 0 rgba(255, 255, 255, ${props.readOnly ? 0 : 0.1})` : ''};

  outline: none;

  ${() => fill(null)}
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};

  ${props => !props.isValid && !props.readOnly && `{
    border-color: rgba(255, 65, 65, 1);
  }`};

  // ETODO Revisit with UX as to when a field should be highlighted
  //&:not(:placeholder-shown):invalid {
  //  border-color: rgba(255, 65, 65, 1);
  //}

  &:disabled {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &:read-only {
    border-left: 0px;
    background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0)'};
    ${props => props.clickable ? 'cursor: pointer;' : 'cursor: default;'}
  }

  &::placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &::-webkit-input-placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &::-ms-input-placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  //noinspection ALL
  &::-moz-input-placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }
`;

const _InputText = styled.input`
  ${baseInputStyles}
`;

const _InputNumber = styled(NumberFormat)`
  ${baseInputStyles}
`;

const _TaxIDMask = styled(TaxIDMask)`
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  padding: 10px 10px 10px 0;

  &:read-only {
    ${props => props.clickable ? 'cursor: pointer;' : 'cursor: default;'}
  }
`;

const _AccountNumberMask = styled(AccountNumberMask)`
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  padding: 10px 10px 10px 0;

  &:read-only {
    ${props => props.clickable ? 'cursor: pointer;' : 'cursor: default;'}
  }
`;

const _TextInputBase = styled.div`
  ${props => fill(props.width)};
  margin: ${props => props.margin || '5px'};
  ${layoutAttribute.css};

  ${props => props.readOnly && props.onClick && `
  :hover {
    border-radius: 3px;
    background-color: ${getBlackWhiteWithOpacity(0.1)};
  };
  transition: background-color 0.5s;
  `};

  &.disabled-input-base {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const MASKING_CHARACTER = 'X';
const FORMAT_ZIPCODE = '#####';
const FORMAT_ZIPCODE_FULL = '#####-####';

function getFormatting(type, value, readOnly) {
  switch (type && type.toLowerCase()) {
    case 'datepicker':
      return {format: '##/##/####', mask: new Array(8).fill(MASKING_CHARACTER)};
    case 'phone':
      return {format: '(###) ###-####', mask: new Array(11).fill(MASKING_CHARACTER)};
    case 'taxid':
      return {format: '#########', mask: new Array(9).fill(MASKING_CHARACTER)};
    case 'zip code':
      return {format: FORMAT_ZIPCODE, mask: new Array(5).fill(MASKING_CHARACTER)};
    case 'zip code full': {
      return {format: readOnly && value.replace(/\D/g, '').length === 5 ? FORMAT_ZIPCODE : FORMAT_ZIPCODE_FULL,
        mask: new Array(9).fill(MASKING_CHARACTER)};
    }
    case 'accountnumber':
      return {format: '##########', mask: new Array(10).fill(MASKING_CHARACTER)};
    default:
      return {format: '', mask: ''};
  }
}

function getValidationMessage(target, type, format, validations) {
  const {maxLength, custom} = (validations || {});

  if (maxLength) {
    if (target.value.length > maxLength) {
      return 'Max Length Exceeded';
    }
  }

  if (!!format) {
    if (type && type.toLowerCase() === 'zip code full') {
      const zip = target.value.split('-')[0];
      const zip4 = target.value.split('-')[1];
      const zip4RegExp = new RegExp(`[^${MASKING_CHARACTER}]`);

      if (zip && zip.includes(MASKING_CHARACTER)) {
        return 'Zip code must be either 5 or 9 digits';
      }

      if (zip4 && (zip4RegExp.test(zip4)) && zip4.includes(MASKING_CHARACTER)) {
        return 'Zip code must be either 5 or 9 digits';
      }
    } else if (target.value.includes(MASKING_CHARACTER)) {
      return 'Invalid value';
    }
  }

  if (custom) {
    return custom(target);
  }

  return '';
}

/**
 * Text Input component
 * @name TextInput
 */
const TextInput = ({
  dataPoint,
  dependency,
  disabled,
  help,
  maxWidth,
  layout,
  onChange,
  onFocus,
  placeholder,
  readOnly,
  required,
  title,
  actionIcon,
  type,
  validations = {maxLength: 255},
  width,
  margin,
  autoCorrect,
  autoComplete,
  id,
  isNotControlled,
  institution,
  institutionName,
}) => {
  const [isValid, setValidity] = useState(true);
  const [val, setVal] = useState(dataPoint.value);
  const [popoverTargetEl, setPopoverTargetEl] = useState(null);
  const [showActionPopover, setShowActionPopover] = useState(false);

  const {name, value = ''} = dataPoint;
  const inputElement = useRef(null);

  const controlledVal = isNotControlled ? undefined : value;

  let opts = {
    id,
    autoCorrect,
    autoComplete,
    disabled,
    maxWidth,
    name,
    placeholder: readOnly ? '-' : placeholder,
    readOnly,
    required,
    spellCheck: false,
    type,
    value: (['phone', 'taxid', 'zip code', 'zip code full', 'accountnumber'].includes(type && type.toLowerCase()) && !readOnly) ? val : controlledVal,
    defaultValue: value,
    isValid,
  };

  if (validations) {
    if (!validations.maxLength) {
      validations.maxLength = 255;
    }
    opts = {...opts, minLength: validations.minLength, pattern: validations.pattern};
  }

  const {format, mask} = getFormatting(type, value, readOnly);

  let _InputBase;
  if (['phone', 'taxid', 'zip code', 'zip code full', 'datepicker'].includes(type && type.toLowerCase())) {
    _InputBase = _InputNumber;
    opts = {...opts, format, mask};
  } else {
    _InputBase = _InputText;
  }

  const validate = (target) => {
    const validationTarget = (['phone', 'taxid', 'zip code', 'zip code full', 'datepicker'].includes(type && type.toLowerCase()))
      ? (target?._reactInternals?.child?.stateNode || target)
      : target;

    log('target', validationTarget);

    const validationMessage = getValidationMessage(validationTarget, type, format, validations);
    validationTarget.setCustomValidity(validationMessage);

    if (!dependency) {
      setValidity(validationTarget.checkValidity());
    }

    // ETODO Come back to this and validation in forms and passwords with UX
    //  if (!target.reportValidity()) {
    //   setValidity(false);
    // } else {
    //   setValidity(true);
    // }
  };

  const renderInput = () => {
    if (type && type.toLowerCase() === 'taxid' && readOnly) {
      return <Row justifySubRow={'left'}>
        <_TaxIDMask taxID={dataPoint.value}
                    readOnly={readOnly}
                    clickable={actionIcon}/>
      </Row>;
    }
    if (type && type.toLowerCase() === 'accountnumber' && readOnly) {
      return <Row justifySubRow={'left'}>
        <_AccountNumberMask number={dataPoint.value}
                            institutionName={institutionName}
                            institution={institution}
                            readOnly={readOnly}
                            clickable={actionIcon}/>
      </Row>;
    }

    return <_InputBase
      ref={inputElement}
      {...opts}
      onChange={e => {
        if (['phone', 'taxid', 'zip code', 'zip code full', 'datepicker'].includes(type && type.toLowerCase()) && !readOnly) {
          setVal(e.target.value);
        }

        onChange(
          e.target.name,
          (type.toLowerCase() === 'phone') ? e.target.value.replace(/\D/g, '') : e.target.value
        );

        if (!isValid) {
          validate(e.target);
        }
      }}
      onBlur={e => validations ? validate(e.target) : ''} // ETODO Revisit with UX to determine best validation alert practice
      onKeyPress={e => validations && e?.key === 'Enter' ? validate(e.target) : ''}
      onFocus={onFocus}
      readOnly={readOnly}
      clickable={actionIcon}
    />;
  };

  useEffect(() => {
    if (['phone', 'taxid', 'zip code', 'zip code full', 'datepicker'].includes(type && type.toLowerCase())) {
      setVal(dataPoint.value);
    }
  }, [readOnly]);

  useEffect(() => {
    if (validations && value && !readOnly) {
      if (inputElement.current) {
        validate(inputElement.current);
      }
    }
  }, [dependency, validations?.message]);

  useEffect(() => {
    if (['phone', 'taxid', 'zip code', 'zip code full', 'datepicker', 'accountnumber'].includes(type && type.toLowerCase())) {return;}

    if (!inputElement.current.focusedElm) {
      setVal(dataPoint.value);
    }
  }, [dataPoint]);

  useEffect(() => {
    function onMouseOver() {
      setShowActionPopover(true);
    }

    function onMouseOut() {
      setShowActionPopover(false);
    }

    if (popoverTargetEl) {
      popoverTargetEl.addEventListener('mouseover', onMouseOver);
      popoverTargetEl.addEventListener('mouseout', onMouseOut);
    }

    return () => {
      if (popoverTargetEl) {
        popoverTargetEl.removeEventListener('mouseover', onMouseOver);
        popoverTargetEl.removeEventListener('mouseout', onMouseOut);
      }
    };
  }, [popoverTargetEl]);

  // TODO Fix Text Input validation so you can click off of fields but still have it be "good" UX for password. - MB
  return <_TextInputBase ref={(readOnly && actionIcon) ? setPopoverTargetEl : null}
                         readOnly={readOnly}
                         onClick={readOnly ? actionIcon?.action : null}
                         className={disabled ? 'disabled-input-base' : ''}
                         width={(readOnly && actionIcon) ? null : width}
                         margin={margin}
                         layout={layout}>
    {title && <TitleLabel
      title={title}
      actionIcon={actionIcon && {
        ...actionIcon,
        spansEntireLabel: true,
        showActionPopover: showActionPopover,
        actionPopoverRef: (!readOnly && actionIcon) ? setPopoverTargetEl : null,
      }}
      help={help}
      clickable={!!actionIcon}
      disableHoverHighlight={readOnly}
    />}
    {renderInput()}
  </_TextInputBase>;
};

export default TextInput;

TextInput.propTypes = {
  /**
   * Text Input Data (name and value to display)
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  /**
   * Data for validation when there is a dependency on a value from another field
   */
  dependency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * If true, then Text Input is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If passed in, then help icon displays per content, title, and type properties
   */
  help: PropTypes.shape({
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  layout: layoutAttribute.propTypes,
  /**
   * Max Width for input field
   */
  maxWidth: PropTypes.string,
  /**
   * The onChange function from Parent for the input field, required if not readOnly
   */
  onChange: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.func}, props, propName, componentName);
    if (props.onChange || props.readOnly) {
      return null;
    }
    return new Error(`If ${componentName} is not readOnly then prop ${propName} is required.`);
  },
  /**
   * Optional onFocus function from Parent
   */
  onFocus: PropTypes.func,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * If true, changes the fill opacity to 0% and removes left border and padding
   */
  readOnly: PropTypes.bool,
  /**
   * If true, then Text Input is required
   */
  required: PropTypes.bool,
  /**
   * Text Input Title Label
   */
  title: PropTypes.string,
  /**
   * If passed in, then an action icon is displayed next to Title Label (will not display without title prop)
   */
  actionIcon: PropTypes.shape({
    /**
     * The icon svg
     */
    icon: PropTypes.string.isRequired,
    /**
     * The action to perform when icon is clicked
     */
    action: PropTypes.func.isRequired,
    /**
     * The popover message/description
     */
    description: PropTypes.string,
  }),
  /**
   * Type of Text Input (text, password, email, phone, zip code, zip code full, taxid, accountnumber)
   */
  type: PropTypes.string.isRequired,
  /**
   * The validations for the value in Text Input, does not apply when readOnly  is true (minLength: minimum number of characters, maxLength: maximum number of characters (Defaults to 255), pattern: pattern to match, custom: a function to validate value)
   */
  validations: PropTypes.shape({
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    pattern: PropTypes.string,
    custom: PropTypes.func,
  }),
  /**
   * The width of the Text Input Element
   */
  width: PropTypes.string,
  /**
   * The optional margin
   */
  margin: PropTypes.string,
  /**
   * Optional HTML ID
   */
  id: PropTypes.string,
  /**
   *
   */
  autoCorrect: PropTypes.oneOf(['on', 'off']),
  /**
   *
   */
  autoComplete: PropTypes.oneOf(['on', 'off']),
  /**
   * toggle to determine if component is controlled (controlled TextInput in MultiInput results in strange behavior)
   */
  isNotControlled: PropTypes.bool,
  /**
   * Optional institution object.
   */
  institution: PropTypes.string,
  /**
   * Optional institution name.
   */
  institutionName: PropTypes.string,
};
